import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ConfirmationService, MessageService } from "primeng/api";
import { BreadcrumbService } from "src/app/breadcrumb.service";
import {
  CEApprovingOffice,
  CEApprovingOfficer,
  CreateCEApprovalOfficeVM,
  UpdateCEApprovalOfficeVM,
} from "src/app/interfaces/contract-extension";
import { User } from "src/app/interfaces/home";
import { ContractExtensionService } from "src/app/services/contract-extension.service";
import { UserService } from "src/app/services/user.service";

@Component({
  selector: "app-contract-extension-approval-setup",
  templateUrl: "./contract-extension-approval-setup.component.html",
  styleUrls: ["./contract-extension-approval-setup.component.scss"],
})
export class ContractExtensionApprovalSetupComponent implements OnInit {
  @ViewChild("formWrapper") public formWrapper: ElementRef;
  approvingOfficeForm: FormGroup;
  allUsers: User[];
  theApprovingUser: User;
  addedApprovingOfficers: CEApprovingOfficer[] = [];
  selectedApprovingOfficer: CEApprovingOfficer;
  fetchingApprovalOffices: boolean;
  allApprovingOffice: CEApprovingOffice[];
  selectedApprovingOffice: CEApprovingOffice;
  addedAppOfficerCols: any[];
  editingApprovingOffice: boolean;
  approvingOfficeToEdit: CEApprovingOffice;
  approvalOfficeCols: any[];

  constructor(
    private fb: FormBuilder,
    public userService: UserService,
    public contractExtensionService: ContractExtensionService,
    private breadcrumbService: BreadcrumbService,
    public confirmationService: ConfirmationService,
    public messageService: MessageService
  ) {
    this.approvingOfficeForm = fb.group({
      ApprovingUser: [""],
      OfficeName: ["", Validators.required],
    });
  }

  ngOnInit(): void {
    this.breadcrumbService.setItems([
      {
        label: "Client Management",
      },
      {
        label: "Contract Extension",
      },
      {
        label: "Approval Setup",
        routerLink: ["/home/client-management/ce-approval-setup"],
      },
    ]);

    this.addedAppOfficerCols = [
      { field: "userProfile.lastName", header: "User Last Name" },
      { field: "createdAt", header: "CreatedAt" },
    ];
    this.approvalOfficeCols = [{ field: "name", header: "Name" }];

    this.FetchAllUsers();
  }

  async FetchAllUsers() {
    this.userService.allUser().subscribe(
      async (r) => {
        var data = r.responseData ?? [];
        this.allUsers = [];
        data.forEach((user) => {
          user.fullName = user.lastName + " " + user.firstName;
          this.allUsers.push(user);
        });
        this.FetchAllApprovingOffice();
      },
      (error) => {
        console.log("Error: " + JSON.stringify(error));
        this.messageService.add({
          severity: "error",
          summary: "Notice",
          detail:
            "Unable to fetch all users at the moment.. Reason: [" +
            (error ? error.error.message : "request failed - permission") +
            "]",
        });
      }
    );
  }

  async FetchAllApprovingOffice() {
    this.fetchingApprovalOffices = true;
    this.contractExtensionService.GetAllCEApprovalOffices().subscribe(
      async (data) => {
        if (data.responseCode != "00") {
          this.messageService.add({
            severity: "error",
            summary: data.responseMsg,
          });
          this.fetchingApprovalOffices = false;
          return;
        }

        this.allApprovingOffice = data.responseData;
        this.fetchingApprovalOffices = false;
      },
      (error) => {
        console.log("Error: " + JSON.stringify(error));
        this.messageService.add({
          severity: "error",
          summary: "Notice",
          detail:
            "Unable to fetch all approving office at the moment.. Reason: [" +
            (error ? error.error.message : "request failed - permission") +
            "]",
        });
        this.fetchingApprovalOffices = false;
      }
    );
  }

  get ApprovingUser() {
    return this.approvingOfficeForm.get("ApprovingUser").value;
  }

  CreateApprovingOffice() {
    this.messageService.add({
      severity: "info",
      summary: "Notice",
      detail: "Creating Approval Office...",
    });

    const postData: CreateCEApprovalOfficeVM = {
      officeName: this.approvingOfficeForm.get("OfficeName").value,
      officersIds: this.addedApprovingOfficers.map((x) => x.userId),
    };

    this.contractExtensionService.CreateCEApprovalOffice(postData).subscribe(
      async (data) => {
        if (data.responseCode != "00") {
          this.messageService.add({
            severity: "error",
            summary: data.responseMsg,
          });
          this.fetchingApprovalOffices = false;
          return;
        }

        this.messageService.add({
          severity: "success",
          summary: "Completed",
          detail: "Approving Office Created Successfully!",
        });

        this.approvingOfficeForm.reset();
        this.addedApprovingOfficers = [];
        this.FetchAllApprovingOffice();
      },
      (error) => {
        console.log("Error: " + JSON.stringify(error));
        this.messageService.add({
          severity: "error",
          summary: "Notice",
          detail:
            "Unable to create approving office at the moment.. Reason: [" +
            (error ? error.error.message : "request failed - permission") +
            "]",
        });
      }
    );
  }

  AddApprovingOfficerToList() {
    if (!this.ApprovingUser) {
      this.messageService.add({
        severity: "error",
        summary: "Failed",
        detail: "You must select the officer to add",
      });
      return;
    }

    let alreadyAdded = false;
    this.addedApprovingOfficers.forEach((item) => {
      if (item.userId == this.ApprovingUser.id) {
        alreadyAdded = true;
      }
    });

    if (alreadyAdded) {
      this.messageService.add({
        severity: "error",
        summary: "Failed",
        detail: "Officer with the same info has already been added to list.",
      });
      return;
    }

    this.addedApprovingOfficers.push({
      userId: this.ApprovingUser.id,
      user: this.ApprovingUser,
    });
    this.theApprovingUser = null;

    this.messageService.add({
      severity: "success",
      summary: "Added",
      detail: "Approving Officer added successfully",
    });
  }

  RemoveOfficer(item: CEApprovingOfficer) {
    this.messageService.add({
      severity: "info",
      summary: "Notice",
      detail: "Removing Approving Officer...",
    });

    const index = this.addedApprovingOfficers.indexOf(item);
    if (index > -1) {
      this.addedApprovingOfficers.splice(index, 1);
    }

    this.messageService.add({
      severity: "success",
      summary: "Removed",
      detail: "Approving Officer Removed successfully",
    });
  }

  CloseEditingApprovalOffice() {
    this.editingApprovingOffice = false;
    this.approvingOfficeForm.reset();
    this.addedApprovingOfficers = [];
    this.approvingOfficeToEdit = null;
  }

  UpdateApprovalOffice() {
    this.messageService.add({
      severity: "info",
      summary: "Notice",
      detail: "Updating Approval Office...",
    });

    let addedApproveOfficerIds: number[] = [];
    this.addedApprovingOfficers.forEach((officer) => {
      addedApproveOfficerIds.push(officer.userId);
    });

    const id = this.approvingOfficeToEdit.id;
    const postData: UpdateCEApprovalOfficeVM = {
      officeName: this.approvingOfficeForm.get("OfficeName").value,
      officersIds: addedApproveOfficerIds,
    };

    this.contractExtensionService
      .UpdateCEApprovalOffice(id, postData)
      .subscribe(
        async (data) => {
          if (data.responseCode != "00") {
            this.messageService.add({
              severity: "error",
              summary: data.responseMsg,
            });
            this.fetchingApprovalOffices = false;
            return;
          }

          this.messageService.add({
            severity: "success",
            summary: "Completed",
            detail: "Approval Office Updated Successfully.",
          });

          this.addedApprovingOfficers = [];
          this.FetchAllApprovingOffice();
          this.editingApprovingOffice = false;
          this.approvingOfficeForm.reset();
        },
        (error) => {
          console.log("Error: " + JSON.stringify(error));
          this.messageService.add({
            severity: "error",
            summary: "Notice",
            detail:
              "Unable to update approving office at the moment.. Reason: [" +
              (error ? error.error.message : "request failed - permission") +
              "]",
          });
        }
      );
  }

  DeleteApprovalOffice(item: CEApprovingOffice) {
    this.confirmationService.confirm({
      message: "Are you sure you want to remove approving office?",
      accept: () => {
        this.messageService.add({
          severity: "info",
          summary: "Notice",
          detail: "Removing approving office...",
        });

        this.contractExtensionService.DeleteCEApprovalOffice(item.id).subscribe(
          async (data) => {
            if (data.responseCode != "00") {
              this.messageService.add({
                severity: "error",
                summary: data.responseMsg,
              });
              this.fetchingApprovalOffices = false;
              return;
            }

            this.messageService.add({
              severity: "success",
              summary: "Removed",
              detail: "Removed successfully",
            });
            this.FetchAllApprovingOffice();
          },
          (error) => {
            console.log("Error: " + JSON.stringify(error));
            this.messageService.add({
              severity: "error",
              summary: "Notice",
              detail:
                "Unable to remove approving office at the moment.. Reason: [" +
                error
                  ? error.error.message
                  : "request failed - permission" + "]",
            });
          }
        );
      },
    });
  }

  EditApprovalOffice(item: CEApprovingOffice) {
    this.editingApprovingOffice = true;
    this.approvingOfficeForm.patchValue({
      OfficeName: item.name,
    });
    this.approvingOfficeToEdit = item;
    this.addedApprovingOfficers = item.officers.filter(
      (x) => x.isDeleted == false
    );
    this.addedApprovingOfficers.forEach((data) => {
      data.user = this.allUsers.find((appUser) => appUser.id == data.userId);
    });

    this.formWrapper.nativeElement.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "start",
    });
  }

  MoveOfficeSequence(option: boolean, sequence: number) {
    this.messageService.add({
      severity: "info",
      summary: "Notice",
      detail: "Moving Approval Office Sequence...",
    });

    this.contractExtensionService
      .MoveCEApprovingOfficeSequence({ sequence: sequence, goesUp: option })
      .subscribe(
        async (data) => {
          if (data.responseCode != "00") {
            this.messageService.add({
              severity: "error",
              summary: data.responseMsg,
            });
            this.fetchingApprovalOffices = false;
            return;
          }

          this.messageService.add({
            severity: "success",
            summary: "Removed",
            detail: "Approving office sequence updated successfully",
          });
          this.FetchAllApprovingOffice();
        },
        (error) => {
          console.log("Error: " + JSON.stringify(error));
          this.messageService.add({
            severity: "error",
            summary: "Notice",
            detail:
              "Unable to move approving office sequence at the moment.. Reason: [" +
              error
                ? error.error.message
                : "request failed - permission" + "]",
          });
        }
      );
  }
}
